import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Maximize2,
  X,
  ChevronLeft,
  ChevronRight,
  Move
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { getColorDotStyle } from "../../admin/common/colorPalettePicker";

const isVideoMedia = (item) => {
  if (!item) return false;
  if (typeof item === "string") {
    return /\.(mp4|webm|mov|avi|mkv|m4v|ogv)(\?.*)?$/i.test(item);
  }
  if (item.type === "video" || item.isVideo) return true;
  if (item.url && /\.(mp4|webm|mov|avi|mkv|m4v|ogv)(\?.*)?$/i.test(item.url)) return true;
  return false;
};

const getCompressedImageUrl = (img) => {
  if (!img) return "";
  if (typeof img === "object") {
    if (img.compressed_url) return img.compressed_url;
    if (img.compressed) {
      if (typeof img.compressed === "string" && img.compressed.startsWith("http")) return img.compressed;
      return img.compressed;
    }
    if (img.compressed_image) return img.compressed_image;
    if (img.url) return getCompressedImageUrl(img.url);
    return "";
  }
  if (typeof img === "string") {
    if (img.includes("compressed_")) return img;
    const lastSlash = img.lastIndexOf("/");
    if (lastSlash !== -1) {
      const dir = img.substring(0, lastSlash + 1);
      const filename = img.substring(lastSlash + 1);
      const dot = filename.lastIndexOf(".");
      const nameWithoutExt = dot !== -1 ? filename.substring(0, dot) : filename;
      return `${dir}compressed_${nameWithoutExt}.webp`;
    }
    return img;
  }
  return "";
};

export default function ProductImageZoomer({
  images = [],
  fallbackImage = "/images/banner5.png",
  activeImageIndex = 0,
  setActiveImageIndex,
  productTitle = "",
  isFavourite = false,
  onToggleFavourite,
  colorPalettes = [],
  onPrevImage,
  onNextImage,
  showFavouriteButton = true,
}) {
  const { t } = useTranslation();

  // Inline Zoom state
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Lightbox Modal state
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lbScale, setLbScale] = useState(1);
  const [lbPosition, setLbPosition] = useState({ x: 0, y: 0 });
  const [lbIsDragging, setLbIsDragging] = useState(false);
  const [lbDragStart, setLbDragStart] = useState({ x: 0, y: 0 });

  const containerRef = useRef(null);
  const lbContainerRef = useRef(null);

  const currentMedia = images && images.length > 0 ? images[activeImageIndex] : { url: fallbackImage };
  const currentUrl = currentMedia?.url || (typeof currentMedia === "string" ? currentMedia : fallbackImage);
  const isVid = isVideoMedia(currentMedia) || isVideoMedia(currentUrl);

  // Reset zoom when image switches
  useEffect(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setLbScale(1);
    setLbPosition({ x: 0, y: 0 });
  }, [activeImageIndex]);

  // Constrain position based on current scale and container dimensions
  const clampPosition = useCallback((newPos, currentScale, containerEl) => {
    if (!containerEl || currentScale <= 1) {
      return { x: 0, y: 0 };
    }
    const rect = containerEl.getBoundingClientRect();
    const maxX = (rect.width * (currentScale - 1)) / 2;
    const maxY = (rect.height * (currentScale - 1)) / 2;

    return {
      x: Math.min(maxX, Math.max(-maxX, newPos.x)),
      y: Math.min(maxY, Math.max(-maxY, newPos.y)),
    };
  }, []);

  // --- Inline Zoom Controls ---
  const handleZoomIn = (e) => {
    e?.stopPropagation();
    setScale((prev) => {
      const next = Math.min(3.5, Number((prev + 0.5).toFixed(1)));
      return next;
    });
  };

  const handleZoomOut = (e) => {
    e?.stopPropagation();
    setScale((prev) => {
      const next = Math.max(1, Number((prev - 0.5).toFixed(1)));
      if (next === 1) setPosition({ x: 0, y: 0 });
      return next;
    });
  };

  const handleResetZoom = (e) => {
    e?.stopPropagation();
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  // Double-click to zoom
  const handleDoubleClick = (e) => {
    if (isVid) return;
    if (scale > 1) {
      handleResetZoom(e);
    } else {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const clickX = e.clientX - rect.left - rect.width / 2;
        const clickY = e.clientY - rect.top - rect.height / 2;
        const targetScale = 2;
        const targetPos = clampPosition(
          { x: -clickX * (targetScale - 1), y: -clickY * (targetScale - 1) },
          targetScale,
          containerRef.current
        );
        setScale(targetScale);
        setPosition(targetPos);
      } else {
        setScale(2);
      }
    }
  };

  // Mouse drag handlers for inline zoom
  const handleMouseDown = (e) => {
    if (scale <= 1 || isVid) return;
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e) => {
    if (!isDragging || scale <= 1) return;
    e.preventDefault();
    const newPos = {
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    };
    setPosition(clampPosition(newPos, scale, containerRef.current));
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch drag handlers for inline zoom
  const [touchStartDist, setTouchStartDist] = useState(0);
  const handleTouchStart = (e) => {
    if (isVid) return;
    if (e.touches.length === 1 && scale > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y,
      });
    } else if (e.touches.length === 2) {
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      setTouchStartDist(dist);
    }
  };

  const handleTouchMove = (e) => {
    if (isVid) return;
    if (e.touches.length === 1 && isDragging && scale > 1) {
      const newPos = {
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y,
      };
      setPosition(clampPosition(newPos, scale, containerRef.current));
    } else if (e.touches.length === 2 && touchStartDist > 0) {
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      const ratio = dist / touchStartDist;
      setScale((prev) => {
        const next = Math.min(3.5, Math.max(1, Number((prev * ratio).toFixed(2))));
        return next;
      });
      setTouchStartDist(dist);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    setTouchStartDist(0);
    if (scale <= 1) setPosition({ x: 0, y: 0 });
  };

  // Attach non-passive wheel listener for smooth wheel zooming
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (scale > 1 || e.deltaY < 0) {
        e.preventDefault();
        const delta = e.deltaY < 0 ? 0.3 : -0.3;
        setScale((prev) => {
          const next = Math.min(3.5, Math.max(1, Number((prev + delta).toFixed(2))));
          if (next === 1) setPosition({ x: 0, y: 0 });
          return next;
        });
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [scale]);

  // --- Lightbox Zoom Controls ---
  const handleLbZoomIn = (e) => {
    e?.stopPropagation();
    setLbScale((prev) => Math.min(4, Number((prev + 0.5).toFixed(1))));
  };

  const handleLbZoomOut = (e) => {
    e?.stopPropagation();
    setLbScale((prev) => {
      const next = Math.max(1, Number((prev - 0.5).toFixed(1)));
      if (next === 1) setLbPosition({ x: 0, y: 0 });
      return next;
    });
  };

  const handleLbReset = (e) => {
    e?.stopPropagation();
    setLbScale(1);
    setLbPosition({ x: 0, y: 0 });
  };

  // Keyboard navigation for Lightbox
  useEffect(() => {
    if (!isLightboxOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsLightboxOpen(false);
      } else if (e.key === "ArrowLeft" && onPrevImage) {
        onPrevImage();
      } else if (e.key === "ArrowRight" && onNextImage) {
        onNextImage();
      } else if (e.key === "+" || e.key === "=") {
        handleLbZoomIn();
      } else if (e.key === "-") {
        handleLbZoomOut();
      } else if (e.key === "0" || e.key.toLowerCase() === "r") {
        handleLbReset();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isLightboxOpen, onPrevImage, onNextImage]);

  // Wheel zoom inside Lightbox
  useEffect(() => {
    if (!isLightboxOpen) return;
    const el = lbContainerRef.current;
    if (!el) return;
    const onLbWheel = (e) => {
      e.preventDefault();
      const delta = e.deltaY < 0 ? 0.25 : -0.25;
      setLbScale((prev) => {
        const next = Math.min(4, Math.max(1, Number((prev + delta).toFixed(2))));
        if (next === 1) setLbPosition({ x: 0, y: 0 });
        return next;
      });
    };
    el.addEventListener("wheel", onLbWheel, { passive: false });
    return () => el.removeEventListener("wheel", onLbWheel);
  }, [isLightboxOpen]);

  // Lock background scroll when Lightbox modal is open
  useEffect(() => {
    if (isLightboxOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isLightboxOpen]);

  return (
    <>
      <div className="flex flex-col gap-4">
        {/* Main Product Image Container */}
        <div
          ref={containerRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onDoubleClick={handleDoubleClick}
          className={`relative banner-box overflow-hidden rounded-2xl shadow-sm border border-zinc-100 flex items-center justify-center bg-black/5 group select-none w-full ${
            isVid ? "" : scale > 1 ? (isDragging ? "cursor-grabbing" : "cursor-grab") : "cursor-zoom-in"
          }`}
          title={isVid ? "" : scale > 1 ? t("product_details.drag_to_explore", { defaultValue: "Drag to explore • Double click to reset" }) : t("product_details.zoom_in", { defaultValue: "Double click or use buttons to zoom in" })}
        >
          {/* Favorite Button (Top-Right) */}
          {showFavouriteButton && onToggleFavourite && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleFavourite();
              }}
              className={`absolute top-4 right-4 w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center transition-all active:scale-90 z-20 shadow-md cursor-pointer ${
                isFavourite
                  ? "bg-pink-500 text-white shadow-pink-200"
                  : "bg-white/90 backdrop-blur-sm text-zinc-600 hover:text-pink-500 hover:bg-white border border-black/5"
              }`}
              title={isFavourite ? "Remove from favourites" : "Add to favourites"}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill={isFavourite ? "currentColor" : "none"}
                stroke="currentColor"
                strokeWidth={isFavourite ? "0" : "1.75"}
                className="w-5 h-5 sm:w-6 sm:h-6 transition-transform"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
                />
              </svg>
            </button>
          )}

          {/* Media Content (Image or Video) */}
          {isVid ? (
            <video
              key={currentUrl}
              src={currentUrl}
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-auto object-contain transition-all duration-300"
              style={{
                transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
                transition: isDragging ? "none" : "transform 0.25s cubic-bezier(0.2, 0, 0, 1)",
                transformOrigin: "center center",
              }}
            />
          ) : (
            <img
              src={currentUrl}
              alt={productTitle || t("common.fallback_product_title")}
              className="w-full h-auto object-contain transition-all duration-300 block"
              draggable={false}
              style={{
                transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
                transition: isDragging ? "none" : "transform 0.25s cubic-bezier(0.2, 0, 0, 1)",
                transformOrigin: "center center",
              }}
            />
          )}

          {/* Prev & Next Arrow Buttons */}
          {images && images.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onPrevImage) onPrevImage();
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 hover:bg-white text-zinc-800 shadow flex items-center justify-center transition-all z-10 active:scale-95"
                title="Previous Image"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onNextImage) onNextImage();
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 hover:bg-white text-zinc-800 shadow flex items-center justify-center transition-all z-10 active:scale-95"
                title="Next Image"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}

          {/* Active Color Badge (Bottom-Left) */}
          {images && images[activeImageIndex]?.color && (
            <span className="absolute bottom-4 left-4 bg-zinc-900/75 text-white text-[10px] font-bold px-3 py-1.5 rounded-full uppercase tracking-wider backdrop-blur-md z-10 shadow-sm">
              Color: {images[activeImageIndex].color}
            </span>
          )}

          {/* Floating Zoom Controls Bar (Bottom-Right) */}
          {!isVid && (
            <div
              className="absolute bottom-4 right-4 flex items-center gap-1.5 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-md border border-zinc-200/80 dark:border-zinc-700/80 shadow-md rounded-full px-2 py-1 z-20 transition-all duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Zoom Out (-) */}
              <button
                type="button"
                onClick={handleZoomOut}
                disabled={scale <= 1}
                className="w-7 h-7 rounded-full flex items-center justify-center text-zinc-700 hover:text-black hover:bg-zinc-100 disabled:opacity-35 disabled:cursor-not-allowed transition-all"
                title={t("product_details.zoom_out", { defaultValue: "Zoom Out" })}
              >
                <ZoomOut className="w-4 h-4" />
              </button>

              {/* Current Zoom Percentage Indicator */}
              <span className="text-[11px] font-bold text-zinc-800 dark:text-zinc-200 px-1.5 min-w-[42px] text-center select-none font-mono">
                {Math.round(scale * 100)}%
              </span>

              {/* Zoom In (+) */}
              <button
                type="button"
                onClick={handleZoomIn}
                disabled={scale >= 3.5}
                className="w-7 h-7 rounded-full flex items-center justify-center text-zinc-700 hover:text-black hover:bg-zinc-100 disabled:opacity-35 disabled:cursor-not-allowed transition-all"
                title={t("product_details.zoom_in", { defaultValue: "Zoom In" })}
              >
                <ZoomIn className="w-4 h-4" />
              </button>

              {/* Reset Zoom */}
              {scale > 1 && (
                <button
                  type="button"
                  onClick={handleResetZoom}
                  className="w-7 h-7 rounded-full flex items-center justify-center text-zinc-700 hover:text-black hover:bg-zinc-100 transition-all ml-0.5"
                  title={t("product_details.reset_zoom", { defaultValue: "Reset Zoom" })}
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                </button>
              )}

              <div className="w-[1px] h-4 bg-zinc-200 mx-0.5" />

              {/* Fullscreen Modal Toggle */}
              <button
                type="button"
                onClick={() => {
                  setLbScale(1.5);
                  setIsLightboxOpen(true);
                }}
                className="w-7 h-7 rounded-full flex items-center justify-center text-zinc-700 hover:text-[var(--primary-400,#ff4081)] hover:bg-zinc-100 transition-all"
                title={t("product_details.fullscreen_view", { defaultValue: "Fullscreen View" })}
              >
                <Maximize2 className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* Zoom & Pan Guidance Toast (shown when zoomed inline) */}
          {scale > 1 && (
            <div className="absolute top-4 left-4 bg-black/65 backdrop-blur-md text-white text-[11px] font-medium px-3 py-1 rounded-full flex items-center gap-1.5 pointer-events-none z-10 transition-opacity animate-fade-in">
              <Move className="w-3 h-3 text-white/80" />
              <span>{t("product_details.drag_to_explore", { defaultValue: "Drag to explore" })}</span>
            </div>
          )}
        </div>

        {/* Thumbnails Row */}
        {images && images.length > 1 && (
          <div className="flex flex-wrap gap-2.5 justify-center mt-1">
            {images.map((img, idx) => {
              const itemVid = isVideoMedia(img);
              const thumbUrl = getCompressedImageUrl(img) || (typeof img === "object" ? img.url : img) || fallbackImage;

              return (
                <div
                  key={idx}
                  onClick={() => setActiveImageIndex(idx)}
                  className={`relative w-16 h-16 rounded-xl overflow-hidden cursor-pointer border-2 transition-all active:scale-95 ${
                    idx === activeImageIndex
                      ? "border-[#FF63B5] scale-105 shadow-md"
                      : "border-zinc-200 hover:border-zinc-300 opacity-80 hover:opacity-100"
                  }`}
                >
                  {itemVid ? (
                    <div className="relative w-full h-full bg-black">
                      <video
                        src={typeof img === "object" ? img.url : img}
                        className="w-full h-full object-cover pointer-events-none opacity-80"
                        muted
                      />
                      <div className="absolute inset-0 flex items-center justify-center text-white bg-black/30">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-red-500">
                          <path fillRule="evenodd" d="M4.5 5.653c0-1.427 1.529-2.33 2.779-1.643l11.54 6.347c1.295.712 1.295 2.573 0 3.286L7.28 19.99c-1.25.687-2.779-.217-2.779-1.643V5.653z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  ) : (
                    <img
                      src={thumbUrl}
                      alt={`Thumbnail ${idx + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        if (fallbackImage && e.target.src !== fallbackImage) {
                          e.target.src = fallbackImage;
                        }
                      }}
                    />
                  )}
                  {img?.color && (
                    <span
                      className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full z-10 shadow-xs border border-white/50"
                      style={getColorDotStyle(img.color, colorPalettes)}
                      title={typeof img.color === "string" ? img.color : img.color?.name}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Fullscreen Lightbox Zoom Modal */}
      {isLightboxOpen && typeof document !== "undefined" && createPortal(
        <div
          className="fixed inset-0 flex flex-col bg-black/95 backdrop-blur-lg select-none"
          style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, width: "100vw", height: "100vh", zIndex: 9999999 }}
        >
          {/* Modal Header Bar */}
          <div
            className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-black/40 z-20"
          >
            {/* Title & Index */}
            <div className="flex items-center gap-3 text-white">
              <span className="font-semibold text-sm sm:text-base truncate max-w-[200px] sm:max-w-md">
                {productTitle || t("common.fallback_product_title")}
              </span>
              {images && images.length > 1 && (
                <span className="text-xs bg-white/10 px-2.5 py-1 rounded-full text-zinc-300 font-mono">
                  {activeImageIndex + 1} / {images.length}
                </span>
              )}
            </div>

            {/* Controls */}
            <div className="flex items-center gap-2">
              {!isVid && (
                <div className="flex items-center gap-1 bg-white/10 backdrop-blur-md rounded-full px-2 py-1 border border-white/15 mr-2">
                  <button
                    type="button"
                    onClick={handleLbZoomOut}
                    disabled={lbScale <= 1}
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white/80 hover:text-white hover:bg-white/15 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                    title={t("product_details.zoom_out", { defaultValue: "Zoom Out" })}
                  >
                    <ZoomOut className="w-4 h-4" />
                  </button>

                  <span className="text-xs font-bold text-white px-2 min-w-[44px] text-center font-mono">
                    {Math.round(lbScale * 100)}%
                  </span>

                  <button
                    type="button"
                    onClick={handleLbZoomIn}
                    disabled={lbScale >= 4}
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white/80 hover:text-white hover:bg-white/15 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                    title={t("product_details.zoom_in", { defaultValue: "Zoom In" })}
                  >
                    <ZoomIn className="w-4 h-4" />
                  </button>

                  {lbScale > 1 && (
                    <button
                      type="button"
                      onClick={handleLbReset}
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white/80 hover:text-white hover:bg-white/15 transition-all ml-1"
                      title={t("product_details.reset_zoom", { defaultValue: "Reset Zoom" })}
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              )}

              {/* Close Button */}
              <button
                type="button"
                onClick={() => setIsLightboxOpen(false)}
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all active:scale-95 cursor-pointer"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Modal Central Zoom Viewport */}
          <div
            ref={lbContainerRef}
            className={`flex-1 relative overflow-hidden flex items-center justify-center ${
              isVid ? "" : lbScale > 1 ? (lbIsDragging ? "cursor-grabbing" : "cursor-grab") : "cursor-zoom-in"
            }`}
            onMouseDown={(e) => {
              if (lbScale <= 1 || isVid) return;
              e.preventDefault();
              setLbIsDragging(true);
              setLbDragStart({ x: e.clientX - lbPosition.x, y: e.clientY - lbPosition.y });
            }}
            onMouseMove={(e) => {
              if (!lbIsDragging || lbScale <= 1) return;
              e.preventDefault();
              const newPos = {
                x: e.clientX - lbDragStart.x,
                y: e.clientY - lbDragStart.y,
              };
              setLbPosition(clampPosition(newPos, lbScale, lbContainerRef.current));
            }}
            onMouseUp={() => setLbIsDragging(false)}
            onMouseLeave={() => setLbIsDragging(false)}
            onDoubleClick={(e) => {
              e.stopPropagation();
              if (isVid) return;
              if (lbScale > 1) {
                handleLbReset(e);
              } else {
                setLbScale(2.5);
              }
            }}
          >
            <div
              className="w-full h-full flex items-center justify-center p-4 pointer-events-none"
              style={{
                transform: `scale(${lbScale}) translate(${lbPosition.x / lbScale}px, ${lbPosition.y / lbScale}px)`,
                transition: lbIsDragging ? "none" : "transform 0.25s cubic-bezier(0.2, 0, 0, 1)",
                transformOrigin: "center center",
              }}
            >
              {isVid ? (
                <video
                  key={currentUrl}
                  src={currentUrl}
                  controls
                  autoPlay
                  className="max-h-[80vh] max-w-[90vw] object-contain pointer-events-auto rounded-lg"
                />
              ) : (
                <img
                  src={currentUrl}
                  alt={productTitle || "Product"}
                  className="max-h-[85vh] max-w-[90vw] object-contain pointer-events-auto"
                  draggable={false}
                />
              )}
            </div>

            {/* Lightbox Prev & Next buttons */}
            {images && images.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onPrevImage) onPrevImage();
                  }}
                  className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-all z-20 backdrop-blur-md active:scale-95"
                  title="Previous"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onNextImage) onNextImage();
                  }}
                  className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-all z-20 backdrop-blur-md active:scale-95"
                  title="Next"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}
          </div>

          {/* Modal Bottom Thumbnail Strip */}
          {images && images.length > 1 && (
            <div
              className="px-6 py-4 bg-black/60 border-t border-white/10 flex items-center justify-center gap-3 overflow-x-auto z-20"
              onClick={(e) => e.stopPropagation()}
            >
              {images.map((img, idx) => {
                const itemVid = isVideoMedia(img);
                const thumbUrl = getCompressedImageUrl(img) || (typeof img === "object" ? img.url : img) || fallbackImage;

                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setActiveImageIndex(idx)}
                    className={`relative w-14 h-14 rounded-xl overflow-hidden border-2 transition-all shrink-0 ${
                      idx === activeImageIndex
                        ? "border-[#FF63B5] scale-110 shadow-lg shadow-pink-500/20"
                        : "border-white/20 opacity-60 hover:opacity-100"
                    }`}
                  >
                    {itemVid ? (
                      <div className="relative w-full h-full bg-black flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-red-500">
                          <path fillRule="evenodd" d="M4.5 5.653c0-1.427 1.529-2.33 2.779-1.643l11.54 6.347c1.295.712 1.295 2.573 0 3.286L7.28 19.99c-1.25.687-2.779-.217-2.779-1.643V5.653z" clipRule="evenodd" />
                        </svg>
                      </div>
                    ) : (
                      <img src={thumbUrl} alt="" className="w-full h-full object-cover" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
}
